import { useEffect, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  resetAdminSetting,
  saveAdminSetting,
  type AdminSetting,
} from '@api/admin-settings';
import { errorText, fromDraft, toDraft } from '../utils/settings';

interface Props {
  setting: AdminSetting;
  onSaved: (setting: AdminSetting) => void;
}

/** One setting: its description, an editor for its type, Save and Reset. */
function SettingEditor({ setting, onSaved }: Props) {
  const headingId = useId();
  const [draft, setDraft] = useState(() => toDraft(setting, setting.value));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const current = toDraft(setting, setting.value);

  // Only a new server value replaces the draft: a reload that brings the
  // same value (another setting saved, a reconnect) keeps what is typed.
  useEffect(() => {
    setDraft(current);
  }, [current]);

  const parsed = fromDraft(setting, draft);
  const changed = parsed !== null && toDraft(setting, parsed) !== current;
  const notWhole =
    setting.type === 'number' && draft.trim() !== '' && parsed === null;
  const emptyList =
    setting.type === 'string_list' && Array.isArray(parsed) && !parsed.length;

  const edit = (next: string) => {
    setDraft(next);
    setSaved(null);
    setError(null);
  };

  const run = (request: () => Promise<AdminSetting>, done: string) => {
    setBusy(true);
    setError(null);
    setSaved(null);
    request()
      .then((next) => {
        setSaved(done);
        // The server's value, even when it reads the same as before.
        setDraft(toDraft(next, next.value));
        onSaved(next);
      })
      .catch((err) => setError(errorText(err)))
      .finally(() => setBusy(false));
  };

  const save = () => {
    if (parsed !== null)
      run(() => saveAdminSetting(setting.key, parsed), 'Saved.');
  };

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={headingId}
      sx={{ border: '1px solid #EAECF0', borderRadius: 2, p: 2 }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography id={headingId} variant="subtitle1" fontWeight={600}>
            {setting.label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#475467' }}>
            {setting.is_default
              ? 'Default'
              : `Changed${setting.updated_by ? ` by ${setting.updated_by}` : ''}${
                  setting.updated_at
                    ? ` on ${new Date(setting.updated_at).toLocaleString()}`
                    : ''
                }`}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: '#475467' }}>
          {setting.description}
        </Typography>

        {setting.type === 'choice' && (
          <RadioGroup
            aria-labelledby={headingId}
            value={draft}
            onChange={(event) => edit(event.target.value)}
          >
            {setting.choices.map((choice) => (
              <FormControlLabel
                key={choice.value}
                value={choice.value}
                control={<Radio size="small" />}
                label={choice.label}
              />
            ))}
          </RadioGroup>
        )}

        {setting.type === 'number' && (
          <TextField
            type="number"
            size="small"
            value={draft}
            error={notWhole}
            onChange={(event) => edit(event.target.value)}
            inputProps={{
              min: setting.min_value ?? undefined,
              max: setting.max_value ?? undefined,
              step: 1,
              'aria-label': setting.label,
            }}
            helperText={
              notWhole
                ? 'Enter a whole number.'
                : setting.min_value !== null && setting.max_value !== null
                  ? `${setting.min_value} to ${setting.max_value}`
                  : undefined
            }
            sx={{ maxWidth: 200 }}
          />
        )}

        {setting.type === 'string_list' && (
          <TextField
            multiline
            minRows={3}
            maxRows={16}
            fullWidth
            size="small"
            value={draft}
            onChange={(event) => edit(event.target.value)}
            inputProps={{ 'aria-label': setting.label }}
            helperText={
              setting.item_help || 'One per line. Empty lines are left out.'
            }
          />
        )}

        {setting.server_override && (
          <Alert severity="info">
            A server setting sets this list today (shown here). A change saved
            here replaces it.
          </Alert>
        )}
        {emptyList && changed && (
          <Alert severity="warning">
            Saving an empty list means letters get none of these.
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {saved && !error && <Alert severity="success">{saved}</Alert>}

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            disabled={busy || !changed}
            onClick={save}
          >
            Save
          </Button>
          {!setting.is_default && (
            <Button
              variant="outlined"
              size="small"
              disabled={busy}
              onClick={() =>
                run(() => resetAdminSetting(setting.key), 'Reset to default.')
              }
            >
              Reset to default
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

export default SettingEditor;
